import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What is the ex-dividend date?',
    a: 'The ex-dividend date (or "ex-date") is the cutoff day for the next dividend payment. To receive the dividend, you must OWN the stock BEFORE the ex-date — meaning you bought it on or before the previous trading day. Buy on the ex-date itself or later, and the previous owner gets the dividend; you wait for the next cycle.',
  },
  {
    q: 'Why does the stock price drop on the ex-date?',
    a: 'On the morning of the ex-date, the stock opens lower by roughly the dividend amount. This is mechanical, not investor sentiment — the company\'s cash account just shrank by the upcoming payout. A $0.83 quarterly dividend means the stock typically opens about $0.83 lower (before other market forces apply). Buying just before ex-date to "capture" the dividend doesn\'t work — you get the dividend AND a matching drop in price, which is a tax wash at best.',
  },
  {
    q: 'What\'s the difference between declaration, record, and pay date?',
    a: 'Declaration date: company announces the dividend (1-4 weeks before ex-date). Ex-date: cutoff for ownership (set by the exchange, usually one business day before record). Record date: company looks at the shareholder list to decide who gets paid (almost always one trading day AFTER ex-date). Pay date: cash actually hits your brokerage account (typically 2-6 weeks after ex-date). DiviDrip surfaces all four on the Stock Modal Dividend Info tab.',
  },
  {
    q: 'What about settlement — T+1?',
    a: 'US stock trades settle T+1 (one business day) as of May 2024. That means you need to BUY on or before the day BEFORE the ex-date to be the record-date holder. Practically, the ex-date is the cutoff you actually care about — your broker handles the settlement timing automatically.',
  },
  {
    q: 'Does DiviDrip show the upcoming ex-date for every stock?',
    a: 'Yes. Open any ticker on the Dashboard, and the Stock Modal shows "Next Ex-Date" and "Next Pay Date" on the Dividend Info tab. The Portfolio page also has a "Pay Sleeve" view that lists every upcoming payment for the stocks you own, sorted by date, so you can see exactly when each cheque lands.',
  },
];

export default function LearnExDividendTimeline() {
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };
  return (
    <LearnPage
      title="What Happens on the Ex-Dividend Date — A Day-by-Day Timeline | DiviDrip"
      description="The five dividend dates every investor confuses — declaration, ex-date, record, pay, settlement. A real JNJ timeline showing what happens at each step and why 'buy the day before ex-date' isn't free money."
      canonical="https://twylightcrow.com/learn/ex-dividend-date-timeline"
      guidePath="/learn/ex-dividend-date-timeline"
      sectionLabel="DRIP & Reinvestment Basics"
      sectionAnchor="basics"
      kicker="Learn · Dividend timing"
      h1="What Happens on the Ex-Dividend Date — A Day-by-Day Timeline"
      datePublished="2026-05-24"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/what-is-drip', label: 'What is DRIP? Dividend reinvestment explained' },
        { to: '/learn/payout-ratio', label: 'The payout ratio — your first dividend safety check' },
        { to: '/learn/monthly-dividend-calendar', label: 'The Monthly Dividend Calendar trick' },
        { to: '/learn/yield-on-cost', label: 'Yield on cost — your real long-term yield' },
        { to: '/glossary', label: 'Glossary — every dividend term defined' },
      ]}
    >
      <p>
        Every dividend payment is a five-act play. Miss the right act and you miss
        the cheque — or worse, you buy thinking you&apos;ll get paid and discover the
        cash goes to the previous owner. The mechanics are simple once you walk
        through them once. Here&apos;s the play, start to finish, using a real{' '}
        <Link to="/stock/JNJ">Johnson &amp; Johnson</Link> dividend as the example.
      </p>

      <h2>The five dates that matter</h2>
      <table className="learn-table">
        <thead>
          <tr><th>Date</th><th>What happens</th><th>What it means for you</th></tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Declaration date</strong></td>
            <td>Board of directors announces the dividend.</td>
            <td>The amount, ex-date, record date, and pay date all become public. Watch for surprise raises or cuts.</td>
          </tr>
          <tr>
            <td><strong>Ex-dividend date</strong></td>
            <td>The market &quot;goes ex&quot; — anyone buying TODAY gets the price WITHOUT the dividend.</td>
            <td>The cutoff. Own it before the ex-date opens, you get paid. Buy on or after the ex-date, the previous owner does.</td>
          </tr>
          <tr>
            <td><strong>Record date</strong></td>
            <td>Company looks at its shareholder list.</td>
            <td>Almost always the next trading day after ex-date. With T+1 settlement, your broker handles this automatically.</td>
          </tr>
          <tr>
            <td><strong>Pay date</strong></td>
            <td>Cash lands in your brokerage account.</td>
            <td>2-6 weeks after the ex-date. The day your dividend cheque shows up.</td>
          </tr>
          <tr>
            <td><strong>Settlement (T+1)</strong></td>
            <td>Your buy or sell trade actually clears.</td>
            <td>As of May 2024, US stocks settle T+1. Practically invisible to you — but it&apos;s why the ex-date is one day before record.</td>
          </tr>
        </tbody>
      </table>

      <h2>A real example — JNJ Q4 dividend timeline</h2>
      <p>
        Pick any Johnson &amp; Johnson quarterly dividend. The cadence looks like this:
      </p>
      <ul>
        <li><strong>Mid-October</strong> — JNJ&apos;s board declares the $1.30 quarterly dividend. Declaration date.</li>
        <li><strong>Late November</strong> — Stock goes ex-dividend at the market open. Anyone owning at yesterday&apos;s close gets paid. Anyone buying today does NOT.</li>
        <li><strong>The next trading day</strong> — Record date. JNJ&apos;s transfer agent snapshots the holder list. You&apos;re on it (or you&apos;re not).</li>
        <li><strong>Mid-December</strong> — Pay date. $1.30 per share lands in your brokerage account. For 100 shares: $130 cash, ready to reinvest or spend.</li>
      </ul>
      <p>
        That whole cycle takes about 8 weeks, declaration to cheque. JNJ repeats it
        four times a year — same rhythm, same names, like clockwork. That predictability
        is exactly why <Link to="/learn/boring-is-beautiful">boring blue chips</Link>{' '}
        are the foundation of most income portfolios.
      </p>

      <h2>The &quot;buy before ex-date&quot; trap</h2>
      <p>
        Every beginner has the same idea: buy the day before the ex-date, collect
        the dividend, then sell. Free money, right?
      </p>
      <p>
        <strong>It doesn&apos;t work.</strong> On the morning of the ex-date, the
        stock opens lower by approximately the dividend amount. If JNJ closes at
        $158.00 the day before ex-date with a $1.30 dividend coming, it tends to
        open near $156.70 on ex-date morning. The market mechanically removes the
        dividend from the share price because the company&apos;s cash account just
        shrank by that amount.
      </p>
      <p>
        Net result of a buy-and-flip:
      </p>
      <ul>
        <li>You bought at $158.00.</li>
        <li>You received $1.30 dividend (taxable as ordinary or qualified income).</li>
        <li>You sold at $156.70.</li>
        <li><strong>Wash.</strong> Possibly worse after short-term capital-loss treatment and the dividend tax.</li>
      </ul>
      <p>
        The only people who win on a same-day flip are the brokers collecting
        commissions and the IRS collecting taxes. Hold for the long term, take
        the dividend as part of your total return, and let{' '}
        <Link to="/learn/yield-on-cost">yield-on-cost</Link> do the compounding work.
      </p>

      <h2>How DiviDrip surfaces every date</h2>
      <p>
        On the Stock Modal&apos;s <strong>Dividend Info</strong> tab, you&apos;ll see:
      </p>
      <ul>
        <li><strong>Last ex-date</strong> — the most recent cutoff.</li>
        <li><strong>Next ex-date</strong> — the upcoming cutoff (often projected from the historical schedule).</li>
        <li><strong>Next pay date</strong> — when the cash actually arrives.</li>
        <li><strong>Frequency</strong> — Monthly / Quarterly / Semi-Annual / Annual.</li>
      </ul>
      <p>
        And on the Portfolio page, the <strong>Pay Sleeve</strong> view chronologically
        lists every upcoming payment across all your holdings — so you can plan cash
        flow, set up DRIP windows, or just enjoy watching the line of cheques marching in.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Try it</h2>
      <p>
        Open any ticker on <Link to="/dashboard">DiviDrip</Link>, click through to
        the Stock Modal, and check the Dividend Info tab. You&apos;ll see the four
        dates laid out cleanly. Then peek at the Portfolio Pay Sleeve to see the
        next 30, 60, 90 days of incoming dividends across everything you own —
        it&apos;s the calmest, most satisfying view on the whole site.
      </p>
    </LearnPage>
  );
}
